import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getClinicalNotes, getConsultationById } from "../../Common/axios_instance/video_https/video.https";
import { Consultation, ClinicalNotes } from "../../Common/interfaces/doctor.interfaces";
import { BottomNav } from "../../Common/custom_components/bottom_nav/BottomNav";
import style from "./consultation_complete.module.css";

export const DoctorConsultationComplete: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const isPatientEnded = location.state?.patientEnded || (id && sessionStorage.getItem(`patientEnded:${id}`) === "true");

  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [notes, setNotes] = useState<ClinicalNotes | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      getConsultationById(id),
      getClinicalNotes(id).catch(() => null),
    ]).then(([consultRes, notesRes]) => {
      if (consultRes.data?.data) setConsultation(consultRes.data.data);
      if (notesRes?.data?.data) setNotes(notesRes.data.data);
    });
  }, [id]);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} min${secs > 0 ? ` ${secs} sec` : ""}`;
  };

  return (
    <div className={style.doctorBody}>
      {/* Header */}
      <div className={style.docHeader}>
        <div className={style.docHeaderLeft}>
          <img
            src="/logo-transparent3.png"
            alt="Pococare"
            style={{ height: "56px" }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
            }}
          />
          <div className={style.docHeaderInfo}>
            <h1>Consultation Complete</h1>
            <span>{consultation?.ticket_id ? String(consultation.ticket_id) : "Loading..."}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={style.completeContainer}>

        {/* Success / Alert Icon */}
        <div className={isPatientEnded ? style.completeAlertIcon : style.completeSuccessIcon}>
          <span className="material-icons-round">
            {isPatientEnded ? "error_outline" : "check_circle"}
          </span>
        </div>

        <div className={style.completeTitle}>
          {isPatientEnded ? "Patient Ended Call" : "Consultation Complete"}
        </div>
        <div className={style.completeSubtitle}>
          {isPatientEnded
            ? "The patient disconnected from the call. Please ensure notes and prescriptions are shared."
            : "Clinical notes saved and prescription shared with the patient successfully."}
        </div>

        {/* Summary Card */}
        <div className={style.summaryCard}>
          <div className={style.summaryRow}>
            <span className={style.summaryLabel}>Patient</span>
            <span className={style.summaryValue}>{consultation?.patient_name || "N/A"}</span>
          </div>
          <div className={style.summaryRow}>
            <span className={style.summaryLabel}>Ticket</span>
            <span className={style.summaryValue}>{consultation?.ticket_id ? String(consultation.ticket_id) : "N/A"}</span>
          </div>
          <div className={style.summaryRow}>
            <span className={style.summaryLabel}>Duration</span>
            <span className={style.summaryValue}>{formatDuration(consultation?.call_duration_seconds)}</span>
          </div>
          <div className={style.summaryRow}>
            <span className={style.summaryLabel}>Mode</span>
            <span className={style.summaryValue} style={{ textTransform: "capitalize" }}>
              {consultation?.consultation_mode || "Video"} Call
            </span>
          </div>
          {notes?.provisional_diagnosis && (
            <div className={style.summaryRow}>
              <span className={style.summaryLabel}>Diagnosis</span>
              <span className={style.summaryValue}>{notes.provisional_diagnosis}</span>
            </div>
          )}
          {notes?.hospitalization_recommended !== undefined && (
            <div className={style.summaryRow}>
              <span className={style.summaryLabel}>Hospitalisation</span>
              <span className={style.summaryValue} style={{ color: notes.hospitalization_recommended ? "var(--success)" : "var(--on-surface)" }}>
                {notes.hospitalization_recommended ? "Recommended" : "Not Required"}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={style.completeActions}>
          <button className={style.btnPrimary} onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </button>
          <button className={style.btnOutlinePrimary} onClick={() => navigate(`/consultation/${id}/detail`)}>
            View Consultation Details
          </button>
        </div>

      </div>

      <BottomNav />
    </div>
  );
};

